/**
 * Landing beacon for the tracked email-click redirect (Plan §5 item 3.4).
 *
 * `GET /api/e/click` already writes `email_link_opened` server-side, before
 * it 302s here — but that alone cannot tell a real person apart from a
 * corporate mail scanner that auto-opens every link in an inbox (Microsoft
 * Defender / Proofpoint / Mimecast all do this at scale). This is the SECOND
 * hop: a token appended to the destination URL that only a real browser,
 * running real page JS, can post back. A scanner that merely follows the
 * redirect never executes this file, so it produces `email_link_opened` with
 * no matching `email_click_confirmed` — the pair is the signal.
 *
 * Deliberately raw `fetch`, not the shared `axios` instance: this has to run
 * before the QueryClient/App even mounts (same reason `captureAttribution`
 * does), and it must never be able to trigger axios's 401-refresh
 * interceptor — `/api/e/confirm` is intentionally unauthenticated, same as
 * the click endpoint itself.
 */

const CONFIRM_PARAM = "ae_click";

export const captureEmailClickConfirmation = (): void => {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get(CONFIRM_PARAM);
    if (!token) return;

    // Fire-and-forget — a failed beacon must never affect the page. Never
    // awaited, and `keepalive` lets the request survive a fast navigation
    // away from this page.
    void fetch("/api/e/confirm", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
      keepalive: true,
    }).catch(() => {
      // Best-effort.
    });

    // Strip the param so a refresh, bookmark, or forwarded link doesn't carry
    // a single-use confirm token around forever, and the address bar the
    // user actually sees stays clean.
    params.delete(CONFIRM_PARAM);
    const query = params.toString();
    const clean = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
    window.history.replaceState(null, "", clean);
  } catch {
    // Best-effort — never let this break the app boot.
  }
};
